
import { useState } from "react";
import { Link } from "react-router-dom";
import { User, LogOut, Shield, Calendar, ChevronDown, Heart, MessageSquare, Bell, Download } from "lucide-react";
import { Button } from "@/components/ui/button";
import { useAdminCheckQuery } from "@/hooks/useAdminCheckQuery";
import { useIsMobile } from "@/hooks/use-mobile";
import { usePWAInstall } from "@/hooks/usePWAInstall";

interface ServiceMenuItem {
  view: string;
  label: string;
  icon: React.ComponentType<any>;
  visible: boolean;
}

interface UserMenuProps {
  displayName: string;
  isAdmin?: boolean;
  isVendor?: boolean;
  onSignOut?: () => Promise<void>;
  serviceMenuItems?: ServiceMenuItem[];
  onServiceViewChange?: (view: string) => void;
  activeServiceView?: string;
}

export const UserMenu = ({
  displayName,
  isAdmin = false,
  isVendor = false,
  onSignOut,
  serviceMenuItems,
  onServiceViewChange,
  activeServiceView,
}: UserMenuProps) => {
  const [isOpen, setIsOpen] = useState(false);
  const { data: isAdminFromQuery } = useAdminCheckQuery();
  const isMobile = useIsMobile();
  const { isInstallable, triggerInstall } = usePWAInstall();
  
  const userIsAdmin = isAdmin || isAdminFromQuery;
  
  const visibleServiceItems = serviceMenuItems?.filter(item => item.visible) || [];

  const handleSignOut = async () => {
    if (onSignOut) {
      setIsOpen(false);
      await onSignOut();
    }
  };

  const handleInstall = async () => {
    setIsOpen(false);
    await triggerInstall();
  };

  return (
    <div className="relative">
      <Button
        variant="ghost"
        onClick={() => setIsOpen(!isOpen)}
        className="flex items-center gap-2 px-3 py-2"
      >
        <User className="h-4 w-4" />
        <span className="hidden md:inline">{displayName}</span>
        <ChevronDown className="h-4 w-4" />
      </Button>

      {isOpen && (
        <div className="absolute right-0 top-full mt-1 w-56 bg-white rounded-md shadow-lg border border-gray-200 z-[100]">
          <div className="py-1">
            <div className="px-4 py-2 border-b border-gray-100">
              <p className="text-sm font-medium text-gray-900">{displayName}</p>
              {/* Only show admin badge for non-vendors or when user is admin but not exclusively a vendor */}
              {userIsAdmin && !isVendor && (
                <span className="inline-flex items-center px-2 py-1 rounded-full text-xs font-medium bg-blue-100 text-blue-800 mt-1">
                  <Shield className="w-3 h-3 mr-1" />
                  Admin
                </span>
              )}
            </div>

            {/* Service navigation for mobile vendors */}
            {isMobile && isVendor && visibleServiceItems.length > 0 && (
              <>
                <div className="px-4 py-2 border-b border-gray-100">
                  <p className="text-xs font-medium text-gray-500 uppercase tracking-wider">Service Management</p>
                </div>
                {visibleServiceItems.map((item) => (
                  <button
                    key={item.view}
                    onClick={() => {
                      onServiceViewChange?.(item.view);
                      setIsOpen(false);
                    }}
                    className={`w-full flex items-center px-4 py-2 text-sm hover:bg-gray-100 ${
                      activeServiceView === item.view 
                        ? 'bg-primary text-primary-foreground hover:bg-primary/90' 
                        : 'text-gray-700'
                    }`}
                  >
                    <item.icon className="mr-3 h-4 w-4" />
                    {item.label}
                  </button>
                ))}
                <div className="border-b border-gray-100 my-1" />
              </>
            )}
            
            {/* Only show profile and other links for non-vendors */}
            {!isVendor ? (
              <>
                <Link
                  to="/my-profile"
                  className="flex items-center px-4 py-2 text-sm text-gray-700 hover:bg-gray-100"
                  onClick={() => setIsOpen(false)}
                >
                  <User className="mr-3 h-4 w-4" />
                  My Profile
                </Link>

                <Link
                  to="/my-bookings"
                  className="flex items-center px-4 py-2 text-sm text-gray-700 hover:bg-gray-100"
                  onClick={() => setIsOpen(false)}
                >
                  <Calendar className="mr-3 h-4 w-4" />
                  My Bookings
                </Link>

                <Link
                  to="/favorites"
                  className="flex items-center px-4 py-2 text-sm text-gray-700 hover:bg-gray-100"
                  onClick={() => setIsOpen(false)}
                >
                  <Heart className="mr-3 h-4 w-4" />
                  My Favorites
                </Link>

                <Link
                  to="/my-reviews"
                  className="flex items-center px-4 py-2 text-sm text-gray-700 hover:bg-gray-100"
                  onClick={() => setIsOpen(false)}
                >
                  <MessageSquare className="mr-3 h-4 w-4" />
                  My Reviews
                </Link>

                <Link
                  to="/my-notifications"
                  className="flex items-center px-4 py-2 text-sm text-gray-700 hover:bg-gray-100"
                  onClick={() => setIsOpen(false)}
                >
                  <Bell className="mr-3 h-4 w-4" />
                  Notifications
                </Link>

                {/* Show Admin Dashboard for admins */}
                {userIsAdmin && (
                  <Link
                    to="/admin"
                    className="flex items-center px-4 py-2 text-sm text-gray-700 hover:bg-gray-100"
                    onClick={() => setIsOpen(false)}
                  >
                    <Shield className="mr-3 h-4 w-4" />
                    Admin Dashboard
                  </Link>
                )}
              </>
            ) : null}

            {/* Install app button - always visible on mobile, only when installable on desktop */}
            {(isInstallable || isMobile) && (
              <button
                onClick={handleInstall}
                className="w-full flex items-center px-4 py-2 text-sm text-gray-700 hover:bg-gray-100"
              >
                <Download className="mr-3 h-4 w-4" />
                Install App {!isInstallable && isMobile ? '(Manual)' : ''}
              </button>
            )}

            {/* Logout button at the bottom */}
            <div className="border-t border-gray-100 mt-1">
              <button
                onClick={handleSignOut}
                className="w-full flex items-center px-4 py-2 text-sm text-gray-700 hover:bg-gray-100"
              >
                <LogOut className="mr-3 h-4 w-4" />
                Sign Out
              </button>
            </div>
          </div>
        </div>
      )}

      {/* Overlay to close dropdown when clicking outside */}
      {isOpen && (
        <div
          className="fixed inset-0 z-[99]"
          onClick={() => setIsOpen(false)}
        />
      )}
    </div>
  );
};
